import type { User } from "firebase/auth";

const getBackendBaseUrl = (): string => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (!raw) {
    throw new Error("Missing REACT_APP_BACKEND_URL environment variable.");
  }
  return raw.replace(/\/+$/, "");
};

async function parseError(response: Response): Promise<string> {
  const data = (await response.json().catch(() => null)) as unknown;
  if (
    data &&
    typeof data === "object" &&
    "detail" in data &&
    typeof (data as { detail?: unknown }).detail === "string"
  ) {
    return (data as { detail: string }).detail;
  }
  return `Backend error (${response.status}).`;
}

export interface OrganizationEmailSendParams {
  subject: string;
  body: string;
  recipientEmail?: string;
  recipientName?: string;
  senderName?: string;
  purpose?: string;
}

export interface OrganizationEmailSendResult {
  id: string;
  organizationId: string;
  recipientEmail: string;
  subject: string;
  status: string;
  purpose: string;
  createdAt: string;
}

export interface OrganizationEmailHistoryItem {
  id?: string;
  subject?: string | null;
  recipientEmail?: string | null;
  status?: string | null;
  purpose?: string | null;
  createdAt?: string | null;
  sentAt?: string | null;
  createdBy?: string | null;
  error?: string | null;
}

export const sendOrganizationEmail = async (
  user: User,
  organizationId: string,
  params: OrganizationEmailSendParams,
): Promise<OrganizationEmailSendResult> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/organizations/${encodeURIComponent(
      organizationId,
    )}/emails`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        subject: params.subject,
        body: params.body,
        recipientEmail: params.recipientEmail,
        recipientName: params.recipientName,
        senderName: params.senderName,
        purpose: params.purpose ?? "marketing",
      }),
    },
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  return (await response.json()) as OrganizationEmailSendResult;
};

export const fetchOrganizationEmails = async (
  user: User,
  organizationId: string,
  limit = 20,
): Promise<OrganizationEmailHistoryItem[]> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const search = new URLSearchParams({ limit: String(limit) });
  const response = await fetch(
    `${baseUrl}/api/v1/platform/organizations/${encodeURIComponent(
      organizationId,
    )}/emails?${search.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  const data = (await response.json()) as { items?: OrganizationEmailHistoryItem[] };
  return Array.isArray(data.items) ? data.items : [];
};

export interface OrganizationEmailSuppressionStatus {
  organizationId: string;
  email: string | null;
  suppressed: boolean;
  suppression?: {
    email?: string;
    reason?: string | null;
    source?: string | null;
    createdAt?: string | null;
  } | null;
}

export const fetchOrganizationEmailSuppression = async (
  user: User,
  organizationId: string,
  email?: string,
): Promise<OrganizationEmailSuppressionStatus> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const search = new URLSearchParams();
  if (email?.trim()) search.set("email", email.trim());
  const qs = search.toString();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/organizations/${encodeURIComponent(
      organizationId,
    )}/email-suppression${qs ? `?${qs}` : ""}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  return (await response.json()) as OrganizationEmailSuppressionStatus;
};

export const clearOrganizationEmailSuppression = async (
  user: User,
  organizationId: string,
  email?: string,
): Promise<{ cleared: boolean; suppressed: boolean; email: string | null }> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const search = new URLSearchParams();
  if (email?.trim()) search.set("email", email.trim());
  const qs = search.toString();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/organizations/${encodeURIComponent(
      organizationId,
    )}/email-suppression${qs ? `?${qs}` : ""}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  return (await response.json()) as {
    cleared: boolean;
    suppressed: boolean;
    email: string | null;
  };
};

export const suppressOrganizationEmail = async (
  user: User,
  organizationId: string,
  email?: string,
  reason = "manual",
): Promise<{ email: string; suppressed: boolean }> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/organizations/${encodeURIComponent(
      organizationId,
    )}/email-suppression`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, reason }),
    },
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  return (await response.json()) as { email: string; suppressed: boolean };
};
